import BaseAPI from './BaseAPI';
import {AccountStorageAPI} from './AccountStorageAPI';

const TEMPLATE_KEY_PREFIX = 'dashboard_template_';

const generateTemplateKey = () =>
	`${TEMPLATE_KEY_PREFIX}${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

// Numeric ids, because the page compares dashboard ids with `> 0` and `+id`.
const V4_DASHBOARD_KEY = /^dashboard_(\d+)$/;
const v4DashboardKey = id => `dashboard_${id}`;

const isPlainObject = value => !!value && typeof value === 'object' && !Array.isArray(value);

// The blob is free-form JSON, so never trust its shape.
const readV4Dashboard = (id, item) => {
	const data = AccountStorageAPI.decodeContent(item?.content);
	const stored = isPlainObject(data) ? data : {};
	return {
		id,
		name: item?.name ?? stored.name ?? '',
		tiles: Array.isArray(stored.tiles) ? stored.tiles : [],
		data: isPlainObject(stored.data) ? stored.data : {},
		locked: stored.locked === true,
	};
};

const storage = new AccountStorageAPI();

export class DashboardAPI extends BaseAPI {

	async create(name) {
		return this.request(`/sec/dashboard`, 'POST', {name});
	}
	async update(dashboard) {
		return this.request(`/sec/dashboard`, 'PUT', {dashboard: dashboard.toJS()});
	}
	async delete(id) {
		return this.request(`/sec/dashboard`, 'DELETE', {id});
	}

	async getPresentationPages() {
		return this.request(`/sec/presentation`, 'GET');
	}
	async savePresentationPage(page) {
		return this.request(`/sec/presentation`, 'POST', page);
	}
	async deletePresentationPage(page) {
		return this.request(`/sec/presentation`, 'DELETE', page);
	}

	async getDashboards(dashboardIds) {
		return this.request(`/sec/dashboard/${JSON.stringify(dashboardIds)}`, 'GET');
	}

	async getTemplates(accountIds) {
		const ids = Array.isArray(accountIds) ? accountIds : [accountIds];
		const results = await Promise.all(ids.map(async (accountId) => {
			try {
				const all = await storage.getAll(accountId);
				return Object.entries(all || {})
					.filter(([key]) => key.startsWith(TEMPLATE_KEY_PREFIX))
					.map(([key, item]) => {
						const data = AccountStorageAPI.decodeContent(item?.content) || {};
						return {
							...data,
							id: key,
							name: item?.name ?? data.name ?? '',
							templateAccountId: accountId,
						};
					});
			} catch (_) {
				return [];
			}
		}));
		return results.flat();
	}
	async saveTemplate(accountId, name, dashboard, existingKey) {
		const key = existingKey && String(existingKey).startsWith(TEMPLATE_KEY_PREFIX)
			? existingKey
			: generateTemplateKey();
		const payload = {...dashboard.toJS(), id: 0, name, templateAccountId: 0};
		return storage.setItem(accountId, key, name, payload);
	}
	async deleteTemplate(accountId, id) {
		return storage.deleteItem(accountId, id);
	}

	// V4 has no dashboard endpoints: each dashboard is one entry of the account's storage.
	async getV4Dashboards(accountId) {
		const all = await storage.getAll(accountId);
		return Object.entries(all || {})
			.map(([key, item]) => [V4_DASHBOARD_KEY.exec(key), item])
			.filter(([match]) => match)
			.map(([match, item]) => readV4Dashboard(+match[1], item))
			.sort((a, b) => a.id - b.id);
	}
	async createV4Dashboard(accountId, name, takenIds = []) {
		const taken = new Set(takenIds.map(Number));
		let id = Date.now();
		while (taken.has(id)) id++;
		const dashboard = {id, name, tiles: [], data: {}, locked: false};
		await storage.setItem(accountId, v4DashboardKey(id), name, dashboard);
		return dashboard;
	}
	async saveV4Dashboard(accountId, dashboard) {
		const value = dashboard.toJS();
		return storage.setItem(accountId, v4DashboardKey(value.id), value.name, value);
	}
	async deleteV4Dashboard(accountId, id) {
		return storage.deleteItem(accountId, v4DashboardKey(id));
	}

}
